<script setup>
  import {
    ref,
    reactive,
    toRefs,
    onMounted,
    onUnmounted,
    computed,
    nextTick,
  } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import dayjs from 'dayjs'
  import {
    getDocMtTaskList,
    deleteTask,
    getTranslateUrl,
    retryDocMtPreprocess,
    refundDocTask,
    refreshDocTwinslatorMtStatus,
  } from '../../../http/api'
  import { DelModal, LoadingModal } from './modal/index'
  import { message } from 'ant-design-vue'

  const route = useRoute()
  const router = useRouter()
  const state = reactive({
    pageNum: 1,
    pageSize: 10,
    total: 0,
    dataSource: [],
    columns: [
      {
        title: '文件名称',
        dataIndex: 'docName',
        slots: { customRender: 'docName' },
        key: 'docName',
      },
      {
        title: '日期',
        dataIndex: 'createTime',
        key: 'createTime',
        slots: { customRender: 'createTime' },
        width: 100,
      },
      {
        title: '语种方向',
        dataIndex: 'language',
        key: 'language',
        slots: { customRender: 'language' },
        width: 100,
      },
      {
        title: '使用引擎',
        dataIndex: 'twinslatorName',
        key: 'twinslatorName',
        width: 100,
      },
      {
        title: '字数',
        dataIndex: 'wordCount',
        key: 'wordCount',
        width: 80,
      },
      {
        title: '翻译进度',
        dataIndex: 'progress',
        key: 'progress',
        slots: { customRender: 'progress' },
        width: 160,
      },
      {
        title: '操作',
        dataIndex: 'operation',
        key: 'operation',
        slots: { customRender: 'operation' },
        width: 230,
      },
    ],
    loading: false,
    delVisible: false,
    columnDetail: {},
    loadingVisible: false,
    fileType: 1,
  })

  const refreshListTimer = ref(null)

  const getList = () => {
    state.loading = true
    getDocMtTaskList({
      pageNum: state.pageNum,
      pageSize: state.pageSize,
      taskClassify: 10,
    })
      .then((res) => {
        const data = res.data.data
        state.dataSource = data.list || []
        state.total = data.total

        let newArr = []
        state.dataSource.forEach((item) => {
          if (
            item.progressStatus == 1 ||
            (item.progressStatus == 3 && item.progress != 100) ||
            item.progressStatus == 5 ||
            item.progressStatus == 7
          ) {
            newArr.push(item.docId)
          }
        })
        if (newArr.length > 0) {
          clearTimeout(refreshListTimer.value)
          refreshFunc(newArr)
        }
      })
      .finally(() => {
        state.loading = false
      })
  }

  const setListStatus = (item) => {
    const newArr = state.dataSource
    newArr.forEach((r) => {
      if (r.docId === item.docId) {
        r.progressStatus = item.progressStatus
        r.progress = item.progress
      }
    })
    nextTick(() => {
      state.dataSource = newArr
    })
  }

  // 轮询当前进度
  const refreshFunc = async (docIds) => {
    let newArr = docIds
    if (newArr.length === 0) {
      clearTimeout(refreshListTimer.value)
      return
    }
    try {
      const res = await refreshDocTwinslatorMtStatus({
        docIds: newArr.join(','),
      })
      const list = res.data.data.list
      list.forEach((item) => {
        setListStatus(item)
        if (
          item.progressStatus == 2 ||
          item.progressStatus == 4 ||
          item.progressStatus == 3
        ) {
          // 2-翻译失败, 3-解析成功, 4-解析失败
          const index = newArr.findIndex((r) => r === item.docId)
          newArr.splice(index, 1)
        }
      })

      refreshListTimer.value = setTimeout(() => {
        refreshFunc(newArr)
      }, 2000)
    } catch (error) {
      clearTimeout(refreshListTimer.value)
    }
  }

  // 分页，页码
  const pageChange = (pageNum) => {
    document.body.scrollTop = document.documentElement.scrollTop = 0
    state.pageNum = pageNum
    getList()
  }

  // 分页，每页条数
  const showSizeChange = (pageNum, pageSize) => {
    document.body.scrollTop = document.documentElement.scrollTop = 0
    state.pageNum = pageNum
    state.pageSize = pageSize
    getList()
  }

  // 显示二次确认删除弹窗
  const showDelModal = (record) => {
    state.delVisible = true
    state.columnDetail = record
  }

  // 删除
  const delFunc = () => {
    deleteTask({ taskIds: state.columnDetail.taskId }).then(() => {
      state.pageNum = 1
      getList()
    })
  }

  //是否不支持导出段段对照
  const isSupportExport = (record) => {
    //是否不支持导出段段对照
    let arrs = [
      'xml',
      'dxf',
      'dwg',
      'json',
      'properties',
      'yaml',
      'yml',
      'xml',
      'xliff',
      'sdlxliff',
      'mqxliff',
      'dita',
    ]
    let fileName = record.docName.split('.').pop().toLowerCase()
    return arrs.includes(fileName)
  }

  //是否不支持导出原文
  const isSupportExportSource = (record) => {
    //是否不支持导出纯原文
    let arrs = ['pdf']
    let fileName = record.docName.split('.').pop().toLowerCase()
    if (arrs.includes(fileName) && record.parseMode == 2) {
      return true
    } else {
      return false
    }
  }
  // 显示loading弹窗
  const showLoadingModel = (record, fileType, isExport) => {
    // 获取文件后缀名
    if (isExport) {
      message.warning('当前稿件格式暂不支持导出该类型')
      return
    }

    state.fileType = fileType
    state.columnDetail = record
    state.loadingVisible = true
  }

  // 深度编辑，前往webcat
  const toWebcat = (record) => {
    if (!record.canWebcatEdit) {
      message.warning('当前稿件格式暂不支持深度编辑')
      return
    }
    const { targetLanguageId, sourceLanguageId, taskId } = record
    getTranslateUrl({
      taskId,
      targetLanguageId,
      sourceLanguageId,
    }).then((res) => {
      const data = res.data
      if (data.result == '1') {
        const url = data.data.url
        window.open(url, '_blank')
      }
    })
  }

  const isNoSupportPreview = (docType) => {
    // console.log(docType)
    return [
      'html',
      'htm',
      'json',
      'properties',
      'yaml',
      'yml',
      'xml',
      'xliff',
      'sdlxliff',
      'mqxliff',
      'srt',
      'docm',
      'dxf',
      'dwg',
      'dita',
    ].includes(docType)
  }
  // 前往预览
  const roPreview = (record) => {
    // if(window.__POWERED_BY_QIANKUN__) {
    //   const url = window.config.host+ '/experiencePreview?isTag=open&taskId=' + taskId
    //   window.open(url,'_blank')
    //   return
    // }

    if (isNoSupportPreview(record.docType)) {
      message.warning('当前稿件格式暂不支持查看')
      return
    }
    let activeKey = route.query.activeKey || ''
    const url =
      window.config.toTwinsHome +
      'experiencePreview?taskId=' +
      record.taskId +
      '&activeKey=' +
      activeKey +
      '&enterpriseId=' + route.query.enterpriseId
    window.open(url, '_blank')
    return
    router.push({
      path: '/experiencePreview',
      query: {
        taskId: record.taskId,
      },
    })
  }

  // 退款
  const refundFunc = (record) => {
    refundDocTask({
      docId: record.docId,
      taskId: record.taskId,
    }).then((res) => {
      const data = res.data
      if (data.result == 1) {
        message.success('退款成功')
        getList()
      }
    })
  }

  // 重试
  const retryFunc = (record) => {
    retryDocMtPreprocess({
      taskId: record.taskId,
    }).then(() => {
      getList()
    })
  }

  onMounted(() => {
    getList()
  })

  onUnmounted(() => {
    clearTimeout(refreshListTimer.value)
  })

  const {
    dataSource,
    columns,
    pageNum,
    pageSize,
    total,
    loading,
    delVisible,
    loadingVisible,
    fileType,
    columnDetail,
  } = toRefs(state)

  defineExpose({
    getList,
  })
</script>

<template>
  <div class="doc-translation-list width-1200">
    <div class="flex-align-center margin-tb-16 padding-l-16">
      <svg aria-hidden="true" class="icon">
        <use xlink:href="#icon-lishi-em"></use>
      </svg>
      <span class="font-size-15 color-333 font-weight-600 margin-l-8">
        翻译记录
      </span>
    </div>
    <div class="list-cotent">
      <a-table
        :dataSource="dataSource"
        :columns="columns"
        :pagination="false"
        :loading="loading"
        :row-key="(record) => record.taskId"
      >
        <template #docName="{ text }">
          <span class="word-break-all">{{ text }}</span>
        </template>
        <template #createTime="{ text }">
          <span>{{ dayjs(+text).format('YYYY.MM.DD') }}</span>
        </template>
        <template #language="{ record, text }">
          <span>{{ record.sourceLangName }} - {{ record.targetLangName }}</span>
        </template>
        <template #progress="{ record, text }">
          <span
            v-if="
              record.progressStatus == 1 ||
              (record.progressStatus == 3 && record.progress != 100) ||
              record.progressStatus == 5 ||
              record.progressStatus == 7
            "
          >
            翻译中
            <a-progress
              :percent="text"
              strokeColor="var(--primary-color)"
              class="width-80 margin-l-10"
            />
          </span>
          <span v-else-if="record.progressStatus == 2" class="color-f55c3f">
            翻译失败
          </span>
          <span v-else-if="record.progressStatus == 4" class="color-f55c3f">
            解析失败
          </span>
          <span
            v-else-if="record.progress == 100 || record.progressStatus == 6"
          >
            翻译完成
          </span>
        </template>
        <template #operation="{ record }">
          <div class="flex-align-center">
            <div
              class="operation-btn"
              :class="{ disabled: isNoSupportPreview(record.docType) }"
              @click="roPreview(record)"
              v-if="
                record.progressStatus == 3 ||
                record.progressStatus == 5 ||
                record.progressStatus == 6
              "
            >
              查看
            </div>
            <a-dropdown
              :trigger="['click']"
              placement="bottomCenter"
              v-if="
                record.progressStatus == 3 ||
                record.progressStatus == 5 ||
                record.progressStatus == 6
              "
            >
              <a class="operation-btn" @click.prevent>导出</a>
              <template #overlay>
                <a-menu
                  v-if="
                    record.docType == 'jpg' ||
                    record.docType == 'png' ||
                    record.docType == 'jpeg'
                  "
                  class="padding-10"
                >
                  <a-menu-item key="3" @click="showLoadingModel(record, 6)">
                    原图
                  </a-menu-item>
                  <a-menu-item key="1" @click="showLoadingModel(record, 1)">
                    译图
                  </a-menu-item>
                  <a-menu-item key="1" @click="showLoadingModel(record, 99)">
                    可编辑译图docx
                  </a-menu-item>
                </a-menu>
                <a-menu v-else class="padding-10">
                  <a-menu-item key="1" @click="showLoadingModel(record, 1)">
                    纯译文
                  </a-menu-item>
                  <a-menu-item key="2" @click="showLoadingModel(record, 3)">
                    <span>并列对照</span>
                  </a-menu-item>
                  <a-menu-item
                    key="3"
                    @click="
                      showLoadingModel(record, 2, isSupportExport(record))
                    "
                  >
                    <span :class="{ 'color-999': isSupportExport(record) }">
                      {{
                        record.parseMode == 2 ? '页页对照' : '段段对照'
                      }}（原文在上，译文在下）
                    </span>
                  </a-menu-item>
                  <a-menu-item
                    key="3"
                    @click="
                      showLoadingModel(record, 5, isSupportExport(record))
                    "
                  >
                    <span :class="{ 'color-999': isSupportExport(record) }">
                      {{
                        record.parseMode == 2 ? '页页对照' : '段段对照'
                      }}（译文在上，原文在下）
                    </span>
                  </a-menu-item>
                  <a-menu-item
                    key="3"
                    @click="
                      showLoadingModel(record, 6, isSupportExportSource(record))
                    "
                  >
                    <span
                      :class="{ 'color-999': isSupportExportSource(record) }"
                    >
                      纯原文
                    </span>
                  </a-menu-item>
                </a-menu>
              </template>
            </a-dropdown>

            <div
              class="operation-btn"
              @click="toWebcat(record)"
              :class="{ disabled: !record.canWebcatEdit }"
              v-if="
                record.progressStatus == 3 ||
                record.progressStatus == 5 ||
                record.progressStatus == 6
              "
            >
              深度编辑
            </div>
            <!--解析失败展示删除按钮-->
            <div
              class="operation-btn"
              v-if="
                record.refundStatus == 1 ||
                record.refundStatus == 2 ||
                record.progressStatus == 4
              "
              @click="showDelModal(record)"
            >
              删除
            </div>
            <div
              class="operation-btn"
              v-if="record.refundStatus === 0"
              @click="refundFunc(record)"
            >
              申请退款
            </div>
          </div>
        </template>
      </a-table>
      <div class="pagination-box" v-if="dataSource.length > 0">
        <a-pagination
          show-size-changer
          show-quick-jumper
          :current="pageNum"
          :page-size="pageSize"
          :total="total"
          show-less-items
          @change="pageChange"
          @showSizeChange="showSizeChange"
        />
      </div>
    </div>
    <!-- 二次确认弹窗 -->
    <DelModal v-model:delVisible="delVisible" @on-del="delFunc" />
    <!-- 导出加载弹窗 -->
    <LoadingModal
      v-model:loadingVisible="loadingVisible"
      :fileType="fileType"
      :columnDetail="columnDetail"
    />
  </div>
</template>

<style lang="scss" scoped>
  .doc-translation-list {
    padding-bottom: 30px;
    .list-title {
      display: flex;
      align-items: center;
      height: 60px;
    }
    .list-cotent {
      padding: 20px 20px 0;
      background-color: #fff;
      box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.04);
      border-radius: 8px;
      border: 1px solid #f2f3f5;
      min-height: calc(100vh - 220px);
      :deep(.ant-table-thead > tr > th) {
        padding: 9px;
        color: #999;
        background: #f6f6f6;
        font-size: 14px;
      }
      :deep(.ant-table-tbody > tr > td) {
        padding: 12px 9px;
      }
    }
    .pagination-box {
      display: flex;
      justify-content: flex-end;
      padding: 20px;
    }
    .operation-btn {
      padding: 5px;
      margin-right: 7px;
      color: var(--primary-color);
      cursor: pointer;
      &.disabled {
        color: #999;
      }
    }
  }
  .width-80 {
    width: 80px;
  }
  .word-break-all {
    word-break: break-all;
  }
  .icon {
    width: 16px;
    height: 16px;
  }
</style>
